import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import DeleteAction from "./DeleteAction";
import ConfirmModal from "../common/ConfirmModal";
import { useTranslation } from "react-i18next";

const DATA_TYPES = ["string", "int", "datetime", "bool", "bigint"];
const DATE_TYPES = new Set(["datetime", "date"]);

const FORMAT_GROUPS = [
  {
    group: "Date only",
    formats: [
      { value: "dd/MM/yyyy",   sample: "15/03/2024",       desc: "Day-first — common in Europe, Australia" },
      { value: "MM/dd/yyyy",   sample: "03/15/2024",       desc: "Month-first — US standard" },
      { value: "yyyy-MM-dd",   sample: "2024-03-15",       desc: "ISO 8601 — recommended for databases" },
      { value: "yyyyMMdd",     sample: "20240315",         desc: "Compact, no separators — EDI / SAP style" },
      { value: "ddMMyyyy",     sample: "15032024",         desc: "Compact day-first" },
      { value: "MMddyyyy",     sample: "03152024",         desc: "Compact month-first" },
      { value: "dd-MM-yyyy",   sample: "15-03-2024",       desc: "Dashed day-first" },
      { value: "dd.MM.yyyy",   sample: "15.03.2024",       desc: "Dotted — common in Germany, Turkey" },
      { value: "yyyy/MM/dd",   sample: "2024/03/15",       desc: "Slashed ISO — common in East Asia" },
      { value: "d MMM yyyy",   sample: "15 Mar 2024",      desc: "Short named month" },
      { value: "d MMMM yyyy",  sample: "15 March 2024",    desc: "Full month name" },
      { value: "MM/yyyy",      sample: "03/2024",          desc: "Month and year only" },
    ],
  },
  {
    group: "Date + Time",
    formats: [
      { value: "yyyy-MM-dd HH:mm:ss",  sample: "2024-03-15 14:30:00",    desc: "ISO 8601 with 24-hour time" },
      { value: "yyyy-MM-ddTHH:mm:ss",  sample: "2024-03-15T14:30:00",    desc: "ISO 8601 strict (T separator)" },
      { value: "yyyy-MM-ddTHH:mm:ssZ", sample: "2024-03-15T14:30:00Z",   desc: "ISO 8601 UTC (Zulu)" },
      { value: "dd/MM/yyyy HH:mm:ss",  sample: "15/03/2024 14:30:00",    desc: "European with 24-hour time" },
      { value: "MM/dd/yyyy HH:mm:ss",  sample: "03/15/2024 14:30:00",    desc: "US with 24-hour time" },
      { value: "dd/MM/yyyy HH:mm",     sample: "15/03/2024 14:30",       desc: "European, minutes only" },
      { value: "dd-MM-yyyy HH:mm",     sample: "15-03-2024 14:30",       desc: "Dashed European, minutes only" },
      { value: "yyyyMMddHHmmss",       sample: "20240315143000",         desc: "Compact datetime — no separators" },
      { value: "yyyyMMddHHmm",         sample: "202403151430",           desc: "Compact, minutes only" },
    ],
  },
  {
    group: "Numeric / Epoch",
    formats: [
      { value: "unix",    sample: "1710509400",       desc: "Unix timestamp — seconds since 1 Jan 1970 UTC" },
      { value: "unix-ms", sample: "1710509400000",    desc: "Unix timestamp — milliseconds since 1 Jan 1970 UTC" },
    ],
  },
];

const ALL_KNOWN_VALUES = new Set(
  FORMAT_GROUPS.flatMap(g => g.formats.map(f => f.value))
);

function normalizeType(raw) {
  const t = (raw || "string").toLowerCase();
  return DATA_TYPES.includes(t) ? t : "string";
}

// ── Format Picker Modal ────────────────────────────────────────────────────────
function FormatPickerModal({ current, onApply, onClose }) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(
    ALL_KNOWN_VALUES.has(current) ? current : (current ? "__custom__" : "")
  );
  const [customVal, setCustomVal] = useState(
    current && !ALL_KNOWN_VALUES.has(current) ? current : ""
  );
  const customRef = useRef(null);

  useEffect(() => {
    if (selected === "__custom__" && customRef.current) customRef.current.focus();
  }, [selected]);

  function handleApply() {
    const fmt = selected === "__custom__" ? customVal.trim() : selected;
    onApply(fmt || undefined);
  }

  function handleKeyDown(e) {
    if (e.key === "Escape") onClose();
  }

  return ReactDOM.createPortal(
    <div className="fpm-backdrop" onMouseDown={onClose} onKeyDown={handleKeyDown}>
      <div className="fpm-modal" onMouseDown={e => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="fpm-header">
          <span className="fpm-title">
            {t("admin.selectDateFormat", { defaultValue: "Select date format" })}
          </span>
          <button className="fpm-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="fpm-body">
          {FORMAT_GROUPS.map(group => (
            <div key={group.group} className="fpm-group">
              <div className="fpm-group-title">{group.group}</div>
              <table className="fpm-table">
                <thead>
                  <tr>
                    <th className="fpm-th fpm-th-pattern">Pattern</th>
                    <th className="fpm-th fpm-th-sample">Sample value</th>
                    <th className="fpm-th fpm-th-desc">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {group.formats.map(fmt => (
                    <tr
                      key={fmt.value}
                      className={`fpm-row${selected === fmt.value ? " fpm-row--selected" : ""}`}
                      onClick={() => setSelected(fmt.value)}
                    >
                      <td className="fpm-td fpm-td-pattern">
                        <code className="fpm-code">{fmt.value}</code>
                      </td>
                      <td className="fpm-td fpm-td-sample">{fmt.sample}</td>
                      <td className="fpm-td fpm-td-desc">{fmt.desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}

          {/* Custom */}
          <div className="fpm-group">
            <div className="fpm-group-title">Custom</div>
            <div
              className={`fpm-custom-row${selected === "__custom__" ? " fpm-row--selected" : ""}`}
              onClick={() => setSelected("__custom__")}
            >
              <code className="fpm-code fpm-code--muted">custom</code>
              <input
                ref={customRef}
                className="fpm-custom-input"
                placeholder="e.g. MM-yyyy or dd/MM/yy HH:mm"
                value={customVal}
                onChange={e => { setCustomVal(e.target.value); setSelected("__custom__"); }}
                onClick={e => e.stopPropagation()}
              />
              <span className="fpm-custom-hint">
                Uses .NET / C# DateTime format tokens
              </span>
            </div>
          </div>
        </div>

        <div className="fpm-footer">
          <button className="fpm-btn fpm-btn--secondary" onClick={onClose}>
            {t("common.cancel", { defaultValue: "Cancel" })}
          </button>
          <button
            className="fpm-btn fpm-btn--primary"
            disabled={!selected || (selected === "__custom__" && !customVal.trim())}
            onClick={handleApply}
          >
            {t("common.apply", { defaultValue: "Apply" })}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── IndexRow ───────────────────────────────────────────────────────────────────
export default function IndexRow({
  index,
  rowIndex,
  isDragging,
  canDelete,
  onDelete,
  onDragStart,
  onDragEnter,
  onDragEnd,
  onUpdateDataType,
  onUpdateLength,
  onToggleDbIndex,
}) {
  const { t } = useTranslation();
  const [localType, setLocalType] = useState(() => normalizeType(index.dataType));
  const [localFormat, setLocalFormat] = useState(index.formatting || "");
  const [showFormatModal, setShowFormatModal] = useState(false);
  // Pending datatype change awaiting confirmation ({ newType, prevType, format }).
  const [typeConfirm, setTypeConfirm] = useState(null);

  useEffect(() => { setLocalType(normalizeType(index.dataType)); }, [index.dataType]);
  useEffect(() => { setLocalFormat(index.formatting || ""); }, [index.formatting]);

  const isDateType = DATE_TYPES.has(localType.toLowerCase());
  // Only text columns carry a meaningful length; int/date/bool do not.
  const isStringType = localType.toLowerCase() === "string";

  // ── Column length (widen) — increase-only inline editor ───────────────────────
  const MAX_COLUMN_LENGTH = 4000; // server enforces the exact per-type cap (NVARCHAR2 → 2000)
  const currentLen = Number.isFinite(index.length) ? index.length : null;
  const [editingLen, setEditingLen] = useState(false);
  const [lenValue, setLenValue] = useState("");
  const lenInputRef = useRef(null);
  // Guards against a double-submit when Enter (or Escape) is immediately followed by blur.
  const lenPendingRef = useRef(false);

  useEffect(() => { if (editingLen && lenInputRef.current) lenInputRef.current.focus(); }, [editingLen]);

  function beginEditLen() {
    lenPendingRef.current = true;
    setLenValue(currentLen != null ? String(currentLen) : "");
    setEditingLen(true);
  }
  function finishLen(commit) {
    if (!lenPendingRef.current) return; // Enter/Escape already handled this edit
    lenPendingRef.current = false;
    setEditingLen(false);
    if (!commit) return;
    const n = parseInt(lenValue, 10);
    if (!Number.isFinite(n) || n <= 0) return;
    if (currentLen != null && n <= currentLen) return; // increase-only
    if (onUpdateLength) onUpdateLength(index.fieldName, n);
  }
  function onLenKeyDown(e) {
    if (e.key === "Enter") { e.preventDefault(); finishLen(true); }
    else if (e.key === "Escape") { e.preventDefault(); finishLen(false); }
  }

  // ── DB index (real physical index) state ──────────────────────────────────────
  const dbStatus = (index.dbIndexStatus || "").toLowerCase(); // "" | pending | building | active | failed | awaitingdba
  const dbIsOn = dbStatus === "active" || dbStatus === "pending" || dbStatus === "building" || dbStatus === "awaitingdba";
  const dbInFlight = dbStatus === "pending" || dbStatus === "building";
  const dbPillLabel =
    dbStatus === "active"      ? t("admin.dbIndexActive",   { defaultValue: "Fast search" }) :
    dbStatus === "building"    ? t("admin.dbIndexBuilding", { defaultValue: "Building…" })   :
    dbStatus === "pending"     ? t("admin.dbIndexPending",  { defaultValue: "Queued" })      :
    dbStatus === "awaitingdba" ? t("admin.dbIndexAwaitingDba", { defaultValue: "Awaiting DBA" }) :
    dbStatus === "failed"      ? t("admin.dbIndexFailed",   { defaultValue: "Failed" })      :
                                 t("admin.dbIndexOff",      { defaultValue: "Standard" });
  const dbSwitchTitle = !onToggleDbIndex
    ? dbPillLabel
    : dbIsOn
      ? t("admin.dbIndexDropTitle",   { defaultValue: "Turn off fast search" })
      : t("admin.dbIndexCreateTitle", { defaultValue: "Turn on fast search" });

  function handleDataTypeChange(e) {
    const newType = e.target.value;
    const prevType = localType;
    if (newType === prevType) return;
    const stillDate = DATE_TYPES.has(newType.toLowerCase());
    // Show the choice in the dropdown right away, but hold the actual change until
    // the user confirms — revert to prevType if they cancel.
    setLocalType(newType);
    if (!stillDate) setLocalFormat("");
    setTypeConfirm({
      newType,
      prevType,
      format: stillDate ? (localFormat || undefined) : undefined,
    });
  }

  function confirmTypeChange() {
    const c = typeConfirm;
    setTypeConfirm(null);
    if (c && onUpdateDataType) onUpdateDataType(c.newType, c.format);
  }

  function cancelTypeChange() {
    const c = typeConfirm;
    setTypeConfirm(null);
    if (c) {
      setLocalType(c.prevType);
      setLocalFormat(index.formatting || "");
    }
  }

  function handleFormatApply(fmt) {
    setLocalFormat(fmt || "");
    setShowFormatModal(false);
    if (onUpdateDataType) onUpdateDataType(localType, fmt);
  }

  // ── Touch swipe-to-reveal delete (mobile has no hover) ────────────────────────
  const REVEAL_PX = 56;
  const canSwipeDelete = !!(canDelete && index.isDeletable);
  const [swipeX, setSwipeX] = useState(0);
  const [swiping, setSwiping] = useState(false);
  const swipeRef = useRef({ startX: 0, startY: 0, base: 0, active: false, horizontal: false });

  function onFieldTouchStart(e) {
    if (!canSwipeDelete) return;
    const tch = e.touches[0];
    swipeRef.current = { startX: tch.clientX, startY: tch.clientY, base: swipeX, active: true, horizontal: false };
  }
  function onFieldTouchMove(e) {
    const s = swipeRef.current;
    if (!s.active) return;
    const tch = e.touches[0];
    const dx = tch.clientX - s.startX;
    const dy = tch.clientY - s.startY;
    if (!s.horizontal) {
      if (Math.abs(dx) > 8 && Math.abs(dx) >= Math.abs(dy)) { s.horizontal = true; setSwiping(true); }
      else if (Math.abs(dy) > 8) { s.active = false; return; } // vertical scroll — let it pass
      else return;
    }
    setSwipeX(Math.max(-REVEAL_PX, Math.min(0, s.base + dx)));
  }
  function onFieldTouchEnd() {
    swipeRef.current.active = false;
    setSwiping(false);
    setSwipeX((prev) => (prev < -REVEAL_PX / 2 ? -REVEAL_PX : 0));
  }
  const revealProgress = Math.min(1, Math.max(0, -swipeX / REVEAL_PX));

  return (
    <>
      <tr
        className={`indexes-row${isDragging ? " is-dragging" : ""}`}
        onDragOver={(e) => { e.preventDefault(); }}
        onDragEnter={(e) => { e.preventDefault(); if (onDragEnter) onDragEnter(rowIndex); }}
        onDragEnd={() => { if (onDragEnd) onDragEnd(); }}
      >
        <td className="indexes-cell-order">
          <span
            className="indexes-drag-handle"
            draggable
            onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; if (onDragStart) onDragStart(rowIndex); }}
            title={t("admin.reorder", { defaultValue: "Drag to reorder" })}
          >⋮⋮</span>
        </td>
        <td className="indexes-cell-field">
          <div
            className="indexes-field-swipe"
            style={{ transform: `translateX(${swipeX}px)`, transition: swiping ? "none" : "transform 0.18s ease" }}
            onTouchStart={onFieldTouchStart}
            onTouchMove={onFieldTouchMove}
            onTouchEnd={onFieldTouchEnd}
          >
            <span className="indexes-field-name">{index.fieldName}</span>
            {canSwipeDelete && <DeleteAction onClick={onDelete} />}
          </div>
          {canSwipeDelete && (
            <button
              type="button"
              className="indexes-field-swipe-delete"
              style={{ opacity: revealProgress, pointerEvents: swipeX < -6 ? "auto" : "none" }}
              onClick={() => { setSwipeX(0); onDelete(); }}
              aria-label={t("admin.deleteButton", { defaultValue: "Delete" })}
              tabIndex={-1}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2m2 0v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
            </button>
          )}
        </td>
        <td className="indexes-cell-type">
          {onUpdateDataType ? (
            <div className="indexes-type-wrap">
              <select
                className="indexes-type-select"
                value={localType}
                onChange={handleDataTypeChange}
                onClick={e => e.stopPropagation()}
              >
                {DATA_TYPES.map(dt => <option key={dt} value={dt}>{dt}</option>)}
              </select>
              {isDateType && (
                <button
                  className={`indexes-format-btn${localFormat ? " indexes-format-btn--set" : ""}`}
                  onClick={e => { e.stopPropagation(); setShowFormatModal(true); }}
                  title={localFormat || t("admin.inputFormat", { defaultValue: "Set input format" })}
                >
                  {localFormat
                    ? <><span className="indexes-format-btn-icon">📅</span>{localFormat}</>
                    : <><span className="indexes-format-btn-icon">+</span>{t("admin.inputFormat", { defaultValue: "Set format" })}</>
                  }
                </button>
              )}
              {onUpdateLength && isStringType && (
                editingLen ? (
                  <span className="indexes-len-edit" onClick={e => e.stopPropagation()}>
                    <input
                      ref={lenInputRef}
                      type="number"
                      className="indexes-len-input"
                      min={currentLen != null ? currentLen + 1 : 1}
                      max={MAX_COLUMN_LENGTH}
                      value={lenValue}
                      onChange={e => setLenValue(e.target.value)}
                      onKeyDown={onLenKeyDown}
                      onBlur={() => finishLen(true)}
                    />
                    <span className="indexes-len-hint">
                      {t("admin.widenIncreaseOnly", { defaultValue: "Increase only" })}
                    </span>
                  </span>
                ) : (
                  <button
                    className="indexes-len-btn"
                    onClick={e => { e.stopPropagation(); beginEditLen(); }}
                    title={t("admin.widenColumn", { defaultValue: "Column length — click to widen" })}
                  >
                    <span className="indexes-len-btn-icon">↔</span>
                    {currentLen != null
                      ? t("admin.fieldLengthValue", { defaultValue: "{{n}} chars", n: currentLen })
                      : t("admin.fieldLength", { defaultValue: "Length" })}
                  </button>
                )
              )}
            </div>
          ) : (
            <span>
              {index.dataType}
              {index.formatting && DATE_TYPES.has(normalizeType(index.dataType)) && (
                <span className="indexes-format-badge">{index.formatting}</span>
              )}
              {currentLen != null && (
                <span className="indexes-len-badge">{t("admin.fieldLengthValue", { defaultValue: "{{n}} chars", n: currentLen })}</span>
              )}
            </span>
          )}
        </td>
        <td className="indexes-cell-created">{index.createdBy}</td>
        <td className="indexes-cell-dbindex">
          <div className="dbindex-control">
            <label
              className={`dbindex-switch${dbInFlight ? " is-busy" : ""}`}
              title={dbSwitchTitle}
            >
              <input
                type="checkbox"
                checked={dbIsOn}
                disabled={!onToggleDbIndex || dbInFlight}
                onClick={(e) => e.stopPropagation()}
                onChange={() => onToggleDbIndex && onToggleDbIndex(index.fieldName, dbIsOn)}
              />
              <span className="dbindex-slider" aria-hidden="true" />
            </label>
            <span
              className={`dbindex-pill dbindex-pill--${dbStatus || "none"}`}
              title={dbStatus === "failed" ? (index.dbIndexError || "") : undefined}
            >
              {dbPillLabel}
            </span>
          </div>
        </td>
      </tr>

      {showFormatModal && (
        <FormatPickerModal
          current={localFormat}
          onApply={handleFormatApply}
          onClose={() => setShowFormatModal(false)}
        />
      )}

      {typeConfirm && ReactDOM.createPortal(
        <ConfirmModal
          open
          variant="info"
          icon="🏷"
          title={t("admin.typeConfirmTitle", { defaultValue: "Change how this field is read?" })}
          name={index.fieldName}
          body={t("admin.typeConfirmBody", {
            type: typeConfirm.newType,
            defaultValue:
              "This changes how “{{field}}” is handled and searched — now as {{type}}. Anything already saved stays as it is; only how new entries are read changes from now on.",
            field: index.fieldName,
          })}
          confirmLabel={t("admin.typeConfirmButton", { defaultValue: "Change type" })}
          onConfirm={confirmTypeChange}
          onCancel={cancelTypeChange}
        />,
        document.body
      )}
    </>
  );
}
